import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatOptionModule } from '@angular/material/core';
import { AppMaterialModule } from 'src/app/app-material.module';
import { BehaviorSubject, combineLatest, map, startWith } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  isValidResult,
  IncumbentCriteriaResult,
  RequestTargetSearchComponent,
  IncumbentDropdownOptions,
} from '../request-target-search/request-target-search.component';
import { RequestClientFilterComponent } from '../request-client-filter/request-client-filter.component';
import { Logging } from '../../../utils/logging';
import { ContractNumberSearchModel } from '../../../models/ContractNumberSearchModel';
import { VccSearchModel } from '../request-target-search/vcc-request-search-modal/vcc-request-search-modal.component';
import { MemberModel } from '../../../services/member/member.service';

enum RequestSelectionStates {
  SELECT_INCUMBENT = 'SELECT_INCUMBENT',
  SELECT_CLIENTS = 'SELECT_CLIENTS',
}

export interface NewRequestResult extends IncumbentCriteriaResult {
  filterCriteria: MemberModel[];
}

@Component({
  selector: 'app-new-request-modal',
  templateUrl: './new-request-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    AppMaterialModule,
    MatOptionModule,
    ReactiveFormsModule,
    RequestTargetSearchComponent,
    RequestClientFilterComponent,
  ],
  styleUrls: ['./new-request-dialog.component.scss'],
})
export class NewRequestDialogComponent {
  readonly RequestSelectionStates = RequestSelectionStates;
  readonly IncumbentDropdownOptions = IncumbentDropdownOptions;

  selfDialogRef: MatDialogRef<
    NewRequestDialogComponent,
    NewRequestResult | null
  >;

  constructor(_selfDialogRef: MatDialogRef<NewRequestDialogComponent>) {
    this.selfDialogRef = _selfDialogRef;
  }

  protected requestSelectionState$ =
    new BehaviorSubject<RequestSelectionStates>(
      RequestSelectionStates.SELECT_INCUMBENT,
    );

  protected selectedIncumbentCriteria$ =
    new BehaviorSubject<IncumbentCriteriaResult>(null);
  protected selectedFilterCriteria$ = new BehaviorSubject<MemberModel[]>([]);

  protected nextIsDisabled$ = combineLatest([
    this.requestSelectionState$,
    this.selectedIncumbentCriteria$.pipe(
      startWith(null),
      map(r => !isValidResult(r)),
      distinctUntilChanged(),
    ),
    this.selectedFilterCriteria$,
  ]).pipe(
    map(([criteriaSelectionState, isInvalidCriteria, selectedFilters]) => {
      switch (criteriaSelectionState) {
        case RequestSelectionStates.SELECT_INCUMBENT:
          return isInvalidCriteria;
        case RequestSelectionStates.SELECT_CLIENTS:
          return selectedFilters.length === 0;
      }
    }),
  );

  onIncumbentSelectionChange(selection: IncumbentCriteriaResult | null) {
    this.selectedIncumbentCriteria$.next(selection);
  }

  onClientsSelectionChange(clients: MemberModel[]) {
    this.selectedFilterCriteria$.next(clients);
  }

  closeModal() {
    this.selfDialogRef.close(null);
  }

  showBackButton(criteriaSelectionState: RequestSelectionStates) {
    return criteriaSelectionState !== RequestSelectionStates.SELECT_INCUMBENT;
  }

  backButtonClicked(criteriaSelectionState: RequestSelectionStates) {
    switch (criteriaSelectionState) {
      case RequestSelectionStates.SELECT_CLIENTS:
        this.selectedIncumbentCriteria$.next(null);
        this.selectedFilterCriteria$.next([]);
        this.requestSelectionState$.next(
          RequestSelectionStates.SELECT_INCUMBENT,
        );
        break;
      default:
        Logging.error('Invalid criteria selection state');
    }
  }

  nextButtonClicked(criteriaSelectionState: RequestSelectionStates) {
    switch (criteriaSelectionState) {
      case RequestSelectionStates.SELECT_INCUMBENT:
        this.requestSelectionState$.next(RequestSelectionStates.SELECT_CLIENTS);
        break;
      case RequestSelectionStates.SELECT_CLIENTS:
        const { resultType, value } =
          this.selectedIncumbentCriteria$.value || {};
        const filterCriteria = this.selectedFilterCriteria$.value || [];

        this.submitNewRequest({
          resultType,
          value,
          filterCriteria,
        });
        break;
      default:
        Logging.error('Invalid criteria selection state');
    }
  }

  getIncumbentDisplayName(selectedIncumbent: IncumbentCriteriaResult) {
    if (!selectedIncumbent) {
      return '';
    }

    const incumbentValue = selectedIncumbent.value;
    switch (selectedIncumbent.resultType) {
      case IncumbentDropdownOptions.CONTRACT_NUMBER:
        return (incumbentValue as ContractNumberSearchModel).contractNumber;
      case IncumbentDropdownOptions.VCC:
        return (incumbentValue as VccSearchModel).vccName;
    }
  }

  private submitNewRequest({
    resultType,
    value,
    filterCriteria,
  }: NewRequestResult) {
    if (resultType === null || value === null) {
      this.selfDialogRef.close(null);
    } else {
      this.selfDialogRef.close({
        resultType,
        value,
        filterCriteria,
      });
    }
  }
}
